import React, { useEffect, useState } from "react";
import { fetchLeaderboards } from "../../../api/leaderboardService";
import QuickPlayModal from "../../../components/modals/QuickPlayModal";
import RoomAnswerInput from "./RoomAnswerInput";
import RoomChat from "./RoomChat";
import RoundHistory from "./RoundHistory";
import RoomDailyTopPlayers from "./RoomDailyTopPlayers";
import RoomHeader from "./RoomHeader";
import RoomImageProposals from "./RoomImageProposals";
import RoomMainImage from "./RoomMainImage";
import RoomRanking from "./RoomRanking";

export default function GameRoomLayout({
  category,
  slug,
  phase,
  remaining,
  roundIndex,
  totalRounds,
  rounds,
  imageUrl,
  submittedBy,
  sourceType,
  correctAnswer,
  debugAnswer,
  onSubmitAnswer,
  answerDisabled,
  lastAnswer,
  answerStatus,
  partialMatchedWords,
  answerFeedback,
  answerHistory,
  roundHistory,
  correctStreak,
  fastStreak,
  wrongStreak,
  performanceNotice,
  players,
  currentPlayerUid,
  isAuthenticated,
  gamesCompletedTick,
  canModeratePlayers,
  chatMessages,
  chatFeedback,
  onSendChatMessage,
  chatDisabled,
  onPlayAnotherCategory,
  flawlessBonusUids,
}) {
  const [isChangeCategoryOpen, setIsChangeCategoryOpen] = useState(false);
  const [dailyTopPlayers, setDailyTopPlayers] = useState([]);

  const categoryName = category?.name || slug || "Categorie";

  useEffect(() => {
    const controller = new AbortController();
    const categorySlug = category?.slug || slug;

    fetchLeaderboards({ type: "daily", category: categorySlug, limit: 3 }, controller.signal)
      .then((data) => {
        setDailyTopPlayers(Array.isArray(data?.results) ? data.results : []);
      })
      .catch(() => {
        setDailyTopPlayers([]);
      });

    return () => controller.abort();
  }, [category?.slug, slug]);

  return (
    <div className="space-y-4 rounded-[28px] border border-slate-200 bg-slate-100 p-2.5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-3 lg:space-y-5 lg:p-4">
      <RoomHeader
        categoryName={categoryName}
        roundIndex={roundIndex}
        totalRounds={totalRounds}
        phase={phase}
        remaining={remaining}
        onChangeCategoryClick={() => setIsChangeCategoryOpen(true)}
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr),360px] 2xl:grid-cols-[280px,minmax(0,1fr),360px] xl:gap-5">
        <aside className="order-3 space-y-4 xl:order-2 2xl:order-1 xl:space-y-5">
          <RoomChat
            isAuthenticated={isAuthenticated}
            messages={chatMessages}
            currentPlayerUid={currentPlayerUid}
            feedback={chatFeedback}
            onSendMessage={onSendChatMessage}
            disabled={chatDisabled}
            categoryName={categoryName}
          />
          <RoomImageProposals
            categorySlug={category?.slug || slug}
            categoryName={categoryName}
            isAuthenticated={isAuthenticated}
            gamesCompletedTick={gamesCompletedTick}
          />
        </aside>

        <section className="order-1 min-w-0 space-y-3 2xl:order-2">
          <div className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.08)]">
            <RoomMainImage
              imageUrl={imageUrl}
              submittedBy={submittedBy}
              sourceType={sourceType}
              correctAnswer={correctAnswer}
              phase={phase}
              categoryName={categoryName}
              roundIndex={roundIndex}
              totalRounds={totalRounds}
              className="rounded-none border-0 shadow-none"
            />
            <div className="border-t border-slate-100" />
            <RoomAnswerInput
              disabled={answerDisabled}
              onSubmitAnswer={onSubmitAnswer}
              debugAnswer={debugAnswer}
              correctStreak={correctStreak}
              fastStreak={fastStreak}
              wrongStreak={wrongStreak}
              phase={phase}
              remaining={remaining}
              partialMatchedWords={partialMatchedWords}
              className="rounded-none border-0 shadow-none"
            />
          </div>

          <RoundHistory
            rounds={rounds}
            roundHistory={roundHistory || {}}
            roundIndex={roundIndex}
            phase={phase}
          />
        </section>

        <aside className="order-2 space-y-8 xl:order-1 2xl:order-3 xl:space-y-10">
          <RoomRanking
            players={players}
            currentPlayerUid={currentPlayerUid}
            canModeratePlayers={canModeratePlayers}
            categorySlug={category?.slug || slug}
            flawlessBonusUids={flawlessBonusUids}
          />
          <RoomDailyTopPlayers players={dailyTopPlayers} />
        </aside>
      </div>

      <QuickPlayModal
        isOpen={isChangeCategoryOpen}
        onClose={() => setIsChangeCategoryOpen(false)}
        onSelectCategory={(selectedCategory) => {
          setIsChangeCategoryOpen(false);
          onPlayAnotherCategory?.(selectedCategory);
        }}
      />
    </div>
  );
}
